/**
 * Pure helpers for the Settings page, kept out of the component so they can be tested.
 */

// Must stay identical to resolveWebinarStart()'s ISO_WITH_OFFSET in
// server/services/autoresponderProcessor.js.
export const isIsoDateTimeWithOffset = (value) => {
  const trimmed = String(value).trim();
  const pattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/;
  if (!pattern.test(trimmed)) return false;
  return !Number.isNaN(new Date(trimmed).getTime());
};

// Same shape the server's resolveCourseSender accepts.
export const isEmailAddress = (value) =>
  /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(String(value).trim());

export const extractYouTubeId = (url) => {
  try {
    const u = new URL(url);
    if (u.hostname === "youtu.be") return u.pathname.slice(1).split("?")[0] || null;
    if (u.hostname.includes("youtube.com")) {
      if (u.pathname.startsWith("/embed/")) return u.pathname.split("/embed/")[1].split("?")[0];
      return u.searchParams.get("v");
    }
  } catch {
    /* not a URL */
  }
  return null;
};

// The message shown under a field, or "" when its value is fine. Empty is always fine.
export const fieldError = (setting, value) => {
  if (!value) return "";
  if (setting.type === "youtube-url" && !extractYouTubeId(value)) {
    return "Not a recognised YouTube URL — enter a youtube.com/watch?v=… or youtu.be/… link.";
  }
  if (setting.type === "iso-datetime" && !isIsoDateTimeWithOffset(value)) {
    return "Use ISO 8601 with a timezone offset, e.g. 2026-09-26T20:00:00+01:00";
  }
  if (setting.type === "email-address" && !isEmailAddress(value)) {
    return "Not a valid email address — it will be ignored and the default sender used instead.";
  }
  return "";
};

// Every field with a visible error in this list, as "Label: message".
export const validationErrors = (fields, formData) =>
  fields
    .map((s) => ({ s, msg: fieldError(s, formData[s.name] || "") }))
    .filter((x) => x.msg)
    .map((x) => `${x.s.label}: ${x.msg}`);

const isTrue = (v) => v === true || v === "true";

// MegaMenu.tsx reads exactly these keys and shows a pillar unless its value is 'false'.
// Hard-coded so the names cannot drift from the site. Old menu_*/submenu_* keys are ignored.
export const PILLAR_TOGGLES = [
  { name: "pillar_what_we_do", title: "What We Do" },
  { name: "pillar_who_we_help", title: "Who We Help" },
  { name: "pillar_resources", title: "Resources" },
  { name: "pillar_about", title: "About" },
].map(({ name, title }) => ({
  name,
  label: `Show "${title}" in the main menu`,
  description: `Display the "${title}" pillar in the website's top navigation`,
  type: "toggle",
}));

// A tick box nobody has saved yet shows the website's own default, which is "shown".
export const toggleChecked = (setting, formData) => {
  const raw = formData[setting.name];
  if (raw !== undefined && raw !== null && raw !== "") return isTrue(raw);
  return true;
};

// The rows to send for one tab. Text fields go out when saved before or edited; a tick box
// goes out only when the user changed it, so saving a tab never rewrites a toggle they left
// alone (or writes one that was never saved).
export const buildSaveItems = (fields, formData, savedNames, touchedNames, category) => {
  const items = [];
  fields.forEach((setting) => {
    const touched = touchedNames.has(setting.name);
    const saved = savedNames.has(setting.name);
    if (setting.type === "toggle" ? !touched : !saved && !touched) return;
    const value =
      setting.type === "toggle"
        ? String(toggleChecked(setting, formData))
        : formData[setting.name] || "";
    items.push({ name: setting.name, value, description: setting.description, category });
  });
  return items;
};
